import React from 'react';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import { ErrorHelperTextFormik } from '~/components/core/Formik/ErrorHelperTextFormik';
import Text from '~/components/core/TextComponents/Text';
import { useVatLabel } from '~/components/hooks/useVatLabel';
import RadioButtonFormik from '~/components/RadioButtonFormik';
import { MonetaryValueTextFieldFormik } from '~/components/TextFieldFormik';
import { safeDecimal } from '~/Utils/decimalUtils';

// The question on its own, for forms that collect the amount elsewhere (a per-allocation amount in
// the split recovery dialog) rather than in the single field below.
export function getVatIncludedValidationSchema(isEnabled: boolean) {
  return { is_vat_included: isEnabled ? Yup.boolean().required('Required') : Yup.boolean() };
}

export function getVatValidationSchema(vatLabel: string, isEnabled: boolean) {
  return {
    ...getVatIncludedValidationSchema(isEnabled),
    vat_amount: Yup.number().when('is_vat_included', (is_vat_included: boolean, currSchema: Yup.NumberSchema) =>
      is_vat_included
        ? currSchema.required('Required').test(
            'is-bigger-than-amount',
            `${vatLabel} amount cannot be bigger than recovery amount`,
            // compared in Decimal, mirroring the server-side guard
            (value: number | undefined, context: Yup.TestContext) =>
              safeDecimal(value).lte(safeDecimal(Number(context.parent.amount)))
          )
        : currSchema
    ),
  };
}

interface VatFormikFieldsProps {
  disabled?: boolean;
  // set to false when the form collects the amount elsewhere (e.g. per allocation) and only the
  // included/not-included question belongs here
  withAmountField?: boolean;
}

const VatFormikFields: React.FC<VatFormikFieldsProps> = ({ disabled = false, withAmountField = true }) => {
  const { classes } = useStyles();
  const vatLabel = useVatLabel();
  const { values } = useFormikContext<{ is_vat_included?: boolean }>();

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="w-1/4">
        <Text variant={Text.VARIANTS.XS}>Is {vatLabel} included?</Text>
        <ErrorHelperTextFormik id="is_vat_included" />
      </div>
      <div className="flex-1">
        <RadioButtonFormik id="is_vat_included" label="No" optionValue={false} disabled={disabled} size="small" />
        <RadioButtonFormik id="is_vat_included" label="Yes" optionValue={true} disabled={disabled} size="small" />
      </div>
      {withAmountField && values.is_vat_included && (
        <div className="w-1/2">
          <MonetaryValueTextFieldFormik
            id="vat_amount"
            label={`${vatLabel} Amount`}
            className={classes.textField}
            disabled={disabled}
            fullWidth
          />
        </div>
      )}
    </div>
  );
};

export default VatFormikFields;
